import React, { useEffect, useState, useCallback } from 'react';
import { Search, X, Edit2, Check, ChevronRight, Briefcase, Users, UsersRound, Mail, MapPin, Landmark, Download } from 'lucide-react';
import api from '../../services/api';
import { toast } from '../../components/ui/sonner';
import { useAuth } from '../../contexts/AuthContext';

// Changing these needs a FinOps administrator (suppliers_routes.SUPPLIER_BANK_FIELDS).
const BANK_FIELDS = new Set(['bank_account_name', 'bank_account_number']);

// Only supplier-ish types remain in FinOps — customers live in Eyvo Pulse now.
const TYPE_TABS = [
  { key: '',         label: 'All' },
  { key: 'supplier', label: 'Suppliers' },
  { key: 'both',     label: 'Both' },
];

const TYPE_BADGE = {
  customer: 'bg-blue-50 text-blue-700 border-blue-200',
  supplier: 'bg-amber-50 text-amber-700 border-amber-200',
  both:     'bg-purple-50 text-purple-700 border-purple-200',
};
const TYPE_ICON = { customer: Briefcase, supplier: Briefcase, both: Users };

const CONTACT_TYPES = ['supplier', 'both'];

export default function SuppliersList() {
  const { isAdmin } = useAuth();
  const [suppliers, setSuppliers] = useState([]);
  const [total, setTotal]         = useState(0);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [typeFilter, setType]     = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [includeCustomers, setIncludeCustomers] = useState(false);
  const [page, setPage]           = useState(1);
  const [selected, setSelected]   = useState(null);
  const [editMode, setEditMode]   = useState(false);
  const [editData, setEditData]   = useState({});
  const [saving, setSaving]       = useState(false);
  const [reactivating, setReactivating] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ page, limit: 50 });
    if (search)     p.set('search', search);
    if (typeFilter) p.set('contact_type', typeFilter);
    p.set('active_only', showInactive ? 'false' : 'true');
    if (includeCustomers) p.set('include_customers', 'true');
    api.get(`/api/suppliers?${p}`)
      .then(r => { setSuppliers(r.data.suppliers || []); setTotal(r.data.total || 0); })
      .finally(() => setLoading(false));
  }, [search, typeFilter, page, showInactive, includeCustomers]);

  useEffect(() => { load(); }, [load]);

  const openSupplier = id => {
    api.get(`/api/suppliers/${id}`).then(r => {
      setSelected(r.data);
      setEditMode(false);
      setEditData({});
    });
  };

  const startEdit = () => {
    setEditData({ ...selected });
    setEditMode(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/api/suppliers/${selected.contact_id}`, editData);
      setSelected(res.data);
      setEditMode(false);
      setEditData({});
      setSuppliers(prev => prev.map(c => c.contact_id === res.data.contact_id ? { ...c, ...res.data } : c));
      toast.success('Supplier saved');
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save supplier');
    } finally {
      setSaving(false);
    }
  };

  const handleReactivate = async (contactId) => {
    setReactivating(contactId);
    try {
      await api.put(`/api/suppliers/${contactId}`, { is_active: true });
      setSuppliers(prev => prev.map(c => c.contact_id === contactId ? { ...c, is_active: true } : c));
      if (selected?.contact_id === contactId) setSelected(s => ({ ...s, is_active: true }));
      toast.success('Supplier reactivated');
    } catch {
      toast.error('Failed to reactivate supplier');
    } finally {
      setReactivating(null);
    }
  };

  const handleDeactivate = async (contactId) => {
    setReactivating(contactId);
    try {
      await api.put(`/api/suppliers/${contactId}`, { is_active: false });
      if (!showInactive) {
        setSuppliers(prev => prev.filter(c => c.contact_id !== contactId));
        if (selected?.contact_id === contactId) setSelected(null);
      } else {
        setSuppliers(prev => prev.map(c => c.contact_id === contactId ? { ...c, is_active: false } : c));
        if (selected?.contact_id === contactId) setSelected(s => ({ ...s, is_active: false }));
      }
      toast.success('Supplier deactivated');
    } catch {
      toast.error('Failed to deactivate supplier');
    } finally {
      setReactivating(null);
    }
  };

  const Field = ({ label, field, type = 'text', options }) => (
    <div>
      <label className="block text-xs text-slate-400 mb-0.5">{label}</label>
      {editMode ? (
        options ? (
          <select value={editData[field] || ''} onChange={e => setEditData(p => ({ ...p, [field]: e.target.value }))}
            className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm focus:outline-none focus:border-indigo-400">
            {options.map(o => <option key={o} value={o}>{o.charAt(0).toUpperCase() + o.slice(1)}</option>)}
          </select>
        ) : (
          <input type={type} value={editData[field] || ''}
            onChange={e => setEditData(p => ({ ...p, [field]: e.target.value }))}
            disabled={BANK_FIELDS.has(field) && !isAdmin()}
            title={BANK_FIELDS.has(field) && !isAdmin() ? 'Only a FinOps administrator can change bank details' : undefined}
            className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm focus:outline-none focus:border-indigo-400 disabled:bg-slate-50 disabled:text-slate-500" />
        )
      ) : (
        <p className="text-sm text-slate-800 min-h-[1.25rem]">{selected[field] || <span className="text-slate-300">—</span>}</p>
      )}
    </div>
  );

  return (
    <div className="flex h-[calc(100vh-60px)] overflow-hidden fade-in" data-testid="suppliers-page">

      {/* Left pane — list */}
      <div className="w-[420px] min-w-[320px] flex flex-col border-r border-slate-200 bg-white">
        {/* Header */}
        <div className="px-4 pt-5 pb-3 border-b border-slate-200">
          <div className="flex items-center justify-between mb-1">
            <h1 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Suppliers</h1>
            <span className="text-xs text-slate-400" data-testid="suppliers-total">{total.toLocaleString()} total</span>
          </div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-[11px] text-slate-400">Client records live in Eyvo Pulse — see <a href="/clients" className="text-indigo-700 hover:underline">Clients</a>.</p>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/suppliers/export/pending-pulse-customers?include_inactive=true`}
              className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-700 hover:text-amber-900"
              data-testid="export-pending-pulse-csv"
              title="Download 81 legacy customer rows (active + inactive) as a CSV ready for Eyvo Pulse import"
            >
              <Download size={11}/> Export customers → Pulse
            </a>
          </div>
          <div className="relative mb-3">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="suppliers-search"
              placeholder="Search name, email, city…"
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              className="w-full pl-8 pr-3 py-1.5 border border-slate-200 rounded-md text-sm focus:outline-none focus:border-indigo-400"
            />
            {search && <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400"><X size={12}/></button>}
          </div>
          {/* Type tabs */}
          <div className="flex items-center justify-between">
            <div className="flex gap-1" data-testid="supplier-type-tabs">
              {TYPE_TABS.map(t => (
                <button key={t.key} onClick={() => { setType(t.key); setPage(1); }}
                  data-testid={`type-tab-${t.key || 'all'}`}
                  className={`px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${typeFilter === t.key ? 'bg-indigo-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                  {t.label}
                </button>
              ))}
            </div>
            <label className="flex items-center gap-1.5 text-[10px] text-slate-500 cursor-pointer select-none ml-2 whitespace-nowrap">
              <input
                type="checkbox"
                data-testid="show-inactive-checkbox"
                checked={showInactive}
                onChange={e => { setShowInactive(e.target.checked); setPage(1); }}
                className="rounded border-slate-300 text-indigo-600 w-3 h-3"
              />
              Show inactive
            </label>
          </div>
          <label className="flex items-center gap-1.5 text-[10px] text-slate-400 cursor-pointer select-none mt-2">
            <input
              type="checkbox"
              data-testid="include-customers-checkbox"
              checked={includeCustomers}
              onChange={e => { setIncludeCustomers(e.target.checked); setPage(1); }}
              className="rounded border-slate-300 text-amber-600 w-3 h-3"
            />
            Include legacy customer rows (pending migration to Pulse)
          </label>
        </div>

        {/* Rows */}
        <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
          {loading ? (
            [...Array(8)].map((_, i) => (
              <div key={i} className="px-4 py-3 flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-slate-100 animate-pulse" />
                <div className="flex-1"><div className="h-3 bg-slate-100 rounded animate-pulse mb-1.5 w-3/4"/><div className="h-2.5 bg-slate-100 rounded animate-pulse w-1/2"/></div>
              </div>
            ))
          ) : suppliers.length === 0 ? (
            <div className="px-4 py-12 text-center text-slate-400 text-sm">
              {search || typeFilter ? 'No suppliers match your filters' : 'No suppliers yet — import to get started'}
            </div>
          ) : (
            suppliers.map(c => {
              const TypeIcon = TYPE_ICON[c.contact_type] || UsersRound;
              const isActive = selected?.contact_id === c.contact_id;
              return (
                <button key={c.contact_id} onClick={() => openSupplier(c.contact_id)}
                  data-testid={`supplier-row-${c.contact_id}`}
                  className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors hover:bg-slate-50 ${isActive ? 'bg-indigo-50 border-r-2 border-indigo-700' : ''}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 ${isActive ? 'bg-indigo-700 text-white' : 'bg-slate-100 text-slate-600'}`}>
                    <TypeIcon size={14} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{c.contact_name}</p>
                    <p className="text-xs text-slate-400 truncate">{c.email_address || c.po_country || c.contact_type}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    {!c.is_active && (
                      <span className="text-[9px] font-medium bg-slate-100 text-slate-400 px-1.5 py-0.5 rounded-full border border-slate-200">
                        Inactive
                      </span>
                    )}
                    <ChevronRight size={12} className="text-slate-300" />
                  </div>
                </button>
              );
            })
          )}
        </div>

        {/* Pagination */}
        {total > 50 && (
          <div className="px-4 py-2 border-t border-slate-200 flex items-center justify-between">
            <button disabled={page === 1} onClick={() => setPage(p => p - 1)}
              className="text-xs text-slate-500 disabled:opacity-40 hover:text-slate-800">← Prev</button>
            <span className="text-xs text-slate-400">Page {page} of {Math.ceil(total / 50)}</span>
            <button disabled={page * 50 >= total} onClick={() => setPage(p => p + 1)}
              className="text-xs text-slate-500 disabled:opacity-40 hover:text-slate-800">Next →</button>
          </div>
        )}
      </div>

      {/* Right pane — detail / edit */}
      {!selected ? (
        <div className="flex-1 flex items-center justify-center text-slate-400">
          <div className="text-center">
            <UsersRound size={40} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm">Select a supplier to view details</p>
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto bg-slate-50" data-testid="supplier-detail-panel">
          {/* Panel header */}
          <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-start justify-between sticky top-0 z-10">
            <div>
              <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                {selected.contact_name}
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border ${TYPE_BADGE[selected.contact_type] || TYPE_BADGE.supplier}`}>
                  {selected.contact_type || 'supplier'}
                </span>
                {selected.contact_type === 'customer' && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full border bg-amber-50 text-amber-800 border-amber-200">
                    Pending Pulse migration
                  </span>
                )}
                {!selected.is_active && <span className="px-2 py-0.5 text-xs rounded-full bg-slate-100 text-slate-400 border border-slate-200">Inactive</span>}
              </h2>
              <p className="text-xs text-slate-400 mt-0.5">{selected.contact_id}</p>
            </div>
            <div className="flex items-center gap-2">
              {editMode ? (
                <>
                  <button onClick={handleSave} disabled={saving}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-900 text-white text-xs font-semibold rounded-md hover:bg-indigo-800 disabled:opacity-50"
                    data-testid="save-supplier-button">
                    <Check size={13}/> {saving ? 'Saving…' : 'Save'}
                  </button>
                  <button onClick={() => { setEditMode(false); setEditData({}); }}
                    className="px-3 py-1.5 border border-slate-200 text-slate-600 text-xs rounded-md hover:bg-slate-50"
                    data-testid="cancel-edit-supplier-button">
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  {selected.is_active ? (
                    <button
                      onClick={() => handleDeactivate(selected.contact_id)}
                      disabled={reactivating === selected.contact_id}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 text-slate-500 text-xs rounded-md hover:bg-slate-100 disabled:opacity-50"
                      data-testid="deactivate-supplier-button"
                    >
                      {reactivating === selected.contact_id ? '…' : 'Deactivate'}
                    </button>
                  ) : (
                    <button
                      onClick={() => handleReactivate(selected.contact_id)}
                      disabled={reactivating === selected.contact_id}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white text-xs font-semibold rounded-md hover:bg-emerald-700 disabled:opacity-50"
                      data-testid="reactivate-supplier-button"
                    >
                      {reactivating === selected.contact_id ? '…' : 'Reactivate'}
                    </button>
                  )}
                  <button onClick={startEdit}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 text-slate-700 text-xs font-medium rounded-md hover:bg-slate-100"
                    data-testid="edit-supplier-button">
                    <Edit2 size={13}/> Edit
                  </button>
                </>
              )}
              <button onClick={() => { setSelected(null); setEditMode(false); }}
                className="text-slate-400 hover:text-slate-700"><X size={16}/></button>
            </div>
          </div>

          <div className="px-6 py-5 space-y-6">
            <Section title="Classification" icon={Users}>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Type"         field="contact_type"  options={CONTACT_TYPES} />
                {editMode ? (
                  <div>
                    <label className="block text-xs text-slate-400 mb-0.5">Status</label>
                    <select value={editData.is_active ? 'true' : 'false'}
                      onChange={e => setEditData(p => ({ ...p, is_active: e.target.value === 'true' }))}
                      className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm focus:outline-none focus:border-indigo-400">
                      <option value="true">Active</option>
                      <option value="false">Inactive</option>
                    </select>
                  </div>
                ) : (
                  <div>
                    <label className="block text-xs text-slate-400 mb-0.5">Status</label>
                    <p className="text-sm text-slate-800">{selected.is_active ? 'Active' : 'Inactive'}</p>
                  </div>
                )}
                <Field label="Legal Name"  field="legal_name" />
                <Field label="Account No." field="account_number" />
              </div>
            </Section>

            <Section title="Contact Info" icon={Mail}>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Email"    field="email_address" type="email" />
                <Field label="Phone"    field="phone_number" />
                <Field label="Mobile"   field="mobile_number" />
                <Field label="Fax"      field="fax_number" />
                <div className="col-span-2">
                  <Field label="Website" field="website" type="url" />
                </div>
              </div>
            </Section>

            <Section title="Billing Address" icon={MapPin}>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2"><Field label="Attention To" field="po_attention_to" /></div>
                <div className="col-span-2"><Field label="Address Line 1" field="po_address_line_1" /></div>
                <div className="col-span-2"><Field label="Address Line 2" field="po_address_line_2" /></div>
                <Field label="City"       field="po_city" />
                <Field label="Region"     field="po_region" />
                <Field label="Postcode"   field="po_zip_code" />
                <Field label="Country"    field="po_country" />
              </div>
            </Section>

            <Section title="Delivery Address" icon={MapPin}>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2"><Field label="Address Line 1" field="sa_address_line_1" /></div>
                <Field label="City"     field="sa_city" />
                <Field label="Region"   field="sa_region" />
                <Field label="Postcode" field="sa_zip_code" />
                <Field label="Country"  field="sa_country" />
              </div>
            </Section>

            <Section title="Tax & Banking" icon={Landmark}>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Tax Number"        field="tax_number" />
                <Field label="Tax Number Type"   field="tax_number_type" />
                <Field label="Bank Account Name" field="bank_account_name" />
                <Field label="Bank Account No."  field="bank_account_number" />
              </div>
            </Section>
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
      <div className="px-4 py-2.5 border-b border-slate-100 bg-slate-50 flex items-center gap-2">
        <Icon size={13} className="text-slate-500" />
        <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">{title}</span>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}
